import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, Smartphone, LineChart, Headphones } from "lucide-react";

const ServicesSection = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);

  const services = [
    {
      id: 1,
      icon: <Monitor className="w-8 h-8" />,
      title: "Website Design",
      description:
        "Modern, responsive websites that captivate your audience and drive meaningful conversions with exceptional user experiences.",
    },
    {
      id: 2,
      icon: <Smartphone className="w-8 h-8" />,
      title: "UI/UX Design",
      description:
        "Intuitive user experiences that delight customers and enhance usability through research-driven design methodologies.",
    },
    {
      id: 3,
      icon: <LineChart className="w-8 h-8" />,
      title: "Digital Solutions",
      description:
        "Comprehensive digital transformation strategies that streamline operations and accelerate business growth.",
    },
    {
      id: 4,
      icon: <Headphones className="w-8 h-8" />,
      title: "Client Support",
      description:
        "24/7 dedicated support and maintenance services ensuring your systems run smoothly and efficiently.",
    },
  ];

  const handleLearnMore = (serviceId) => {
    navigate(`/services?service=${serviceId}`);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let currentIndex = 0;
    let isScrolling = false;

    const scroll = () => {
      if (isScrolling) return;

      const cardWidth =
        container.querySelector(".service-card")?.offsetWidth || 0;
      const gap = 24; // gap-6 = 24px
      const scrollAmount = cardWidth + gap;

      isScrolling = true;
      currentIndex++;

      // Loop back to start after showing all 4 cards
      if (currentIndex >= services.length) {
        currentIndex = 0;
      }

      // Scroll to the calculated position
      container.scrollTo({
        left: currentIndex * scrollAmount,
        behavior: "smooth",
      });

      setTimeout(() => {
        isScrolling = false;
      }, 800);
    };

    const interval = setInterval(scroll, 3000);

    return () => clearInterval(interval);
  }, [services.length]);

  return (
    <div className="py-12 sm:py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
            OURSERVICES
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
            Comprehensive IT Solutions
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4">
            We deliver innovative IT solutions that empower your business to
            adapt, scale, and succeed
            <br className="hidden sm:block" />
            in a future technology-driven world by accelerating.
          </p>
        </div>

        {/* Mobile & Tablet: Auto-scrolling carousel */}
        <div
          ref={scrollContainerRef}
          className="lg:hidden overflow-x-auto scrollbar-hide -mx-4 px-4 mb-8"
        >
          <div className="flex gap-6 pb-4">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card bg-white p-6 sm:p-7 rounded-2xl shadow-lg hover:shadow-xl transition flex-shrink-0 w-[85vw] sm:w-[calc(50%-12px)] max-w-sm"
              >
                <div className="bg-blue-500 text-white w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mb-4 sm:mb-5">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  {service.description}
                </p>
                <button
                  onClick={() => handleLearnMore(service.id)}
                  className="text-blue-500 font-medium hover:underline text-sm sm:text-base cursor-pointer"
                >
                  Learn More →
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: Grid (4 items) */}
        <div className="hidden lg:grid grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition"
            >
              <div className="bg-blue-500 text-white w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {service.title}
              </h3>
              <p className="text-gray-600 mb-4">{service.description}</p>
              <button
                onClick={() => handleLearnMore(service.id)}
                className="text-blue-500 font-medium hover:underline cursor-pointer"
              >
                Learn More →
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <button
            onClick={() => navigate("/services")}
            className="bg-blue-500 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full hover:bg-blue-600 transition text-sm sm:text-base cursor-pointer"
          >
            See All Services
          </button>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ServicesSection;
